/**
 * Upah borongan jahit per FA — VONIS-24SEP-BORONGAN (Owner 24 Sep 2026).
 *
 * Tarif dikunci SAAT FA DIBUAT (tidak berlaku surut), jadi rencana upah & HPP upah FA sudah
 * terlihat sejak awal. Kartu ini menampilkan:
 *   - rincian komponen per baris item (dasar, kerah, lengan panjang, raglan) × qty × tarif beku;
 *   - "tarif belum lengkap" bila ada komponen tanpa tarif — serah terima Jahit ditolak;
 *   - group jahit yang ditugaskan (Admin Produksi/KC/Owner menugaskan; boleh beberapa);
 *   - ajukan koreksi tarif yang salah isi (KC/Owner; disetujui Owner — BUSINESS-19).
 * Rupiah hanya tampil untuk Owner/Kepala Cabang/Finance (backend yang menyaringnya, K68).
 * Peran tanpa hak membaca serah terima menerima 403 → kartu tidak dirender.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { StatusBadge } from "./StatusBadge";
import { labelBarisItem } from "../../lib/serahTerimaJenis";
import { toast } from "sonner";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { formatDate } from "../../lib/format";

const rp = (n) => `Rp${Number(n || 0).toLocaleString("id-ID")}`;

function pesan(e, cadangan) {
  const d = e?.response?.data?.detail;
  return (d && (d.message || (typeof d === "string" ? d : null))) || cadangan;
}

export default function UpahBoronganFa({ faId, onBerubah }) {
  const { roleCode } = useAuth();
  const [data, setData] = useState(null);
  const [tertutup, setTertutup] = useState(false);
  const [groups, setGroups] = useState([]);
  const [pilihGroup, setPilihGroup] = useState(null);
  const [koreksi, setKoreksi] = useState(null); // {work_type_id, label, rate}
  const [benar, setBenar] = useState("");
  const [alasan, setAlasan] = useState("");
  const bolehTugaskan = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI"].includes(roleCode);
  const bolehKoreksi = ["OWNER", "KEPALA_CABANG"].includes(roleCode);

  const muat = useCallback(async () => {
    try {
      const r = await api.get(`/borongan/fa/${faId}/upah`);
      setData(r.data);
      setPilihGroup((r.data.group_tertugas || []).map((g) => g.sewing_group_id));
    } catch (e) {
      if (e?.response?.status === 403) setTertutup(true);
    }
  }, [faId]);

  useEffect(() => { muat(); }, [muat]);
  useEffect(() => {
    if (!bolehTugaskan) return;
    api.get("/group-jahit").then(({ data: d }) => setGroups(d.items || d || [])).catch((e) => { setGroups([]); toast.error(apiErrorMessage(e, "Daftar group jahit gagal dimuat")); });
  }, [bolehTugaskan]);

  if (tertutup || !data) return null;

  const simpanGroup = async () => {
    try {
      await api.put(`/borongan/fa/${faId}/group-jahit`, { group_ids: pilihGroup || [] });
      toast.success("Group jahit FA disimpan.");
      muat();
      if (onBerubah) onBerubah();
    } catch (e) {
      toast.error(pesan(e, "Gagal menyimpan group jahit"));
    }
  };

  const ajukanKoreksi = async () => {
    const n = Number(benar);
    if (!Number.isInteger(n) || n <= 0) { toast.error("Tarif benar wajib bilangan bulat > 0."); return; }
    if (alasan.trim().length < 10) { toast.error("Alasan minimal 10 karakter."); return; }
    try {
      const r = await api.post("/borongan/koreksi-tarif", {
        fa_id: faId, work_type_id: koreksi.work_type_id, rate_benar_idr: n, alasan: alasan.trim(),
      });
      toast.success(r.data?.status === "APPROVED" ? "Koreksi berlaku." : "Koreksi diajukan — menunggu Owner.");
      setKoreksi(null); setBenar(""); setAlasan("");
    } catch (e) {
      toast.error(pesan(e, "Gagal mengajukan koreksi"));
    }
  };

  // T97: label bahasa staf + ukuran ("Jersey set celana hitam/putih · S"), bukan kode mentah.
  const itemNama = Object.fromEntries((data.items || []).map((i) => [i.id, labelBarisItem(i)]));
  const regionGroups = groups.filter((g) => g.is_active !== false && (!data.region_id || g.region_id === data.region_id));

  return (
    <Card className="p-4 space-y-3" data-testid="upah-borongan-fa">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium">Upah borongan jahit</div>
          <div className="text-xs text-muted-foreground">
            Tarif dikunci pada tanggal FA dibuat: {data.tanggal_tarif ? formatDate(data.tanggal_tarif) : "—"}
          </div>
        </div>
        {data.tarif_lengkap ? (
          <StatusBadge tone="success">Tarif lengkap</StatusBadge>
        ) : (
          <StatusBadge tone="danger" testId="badge-tarif-belum-lengkap">Tarif belum lengkap</StatusBadge>
        )}
      </div>

      {!data.tarif_lengkap && (
        <p className="rounded border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">
          Komponen tanpa tarif: {(data.komponen_kurang || []).map((k) => k.label || k.jenis).join(", ")}.
          Serah terima Jahit ditolak sampai tarifnya diisi di Master › Tarif Borongan.
        </p>
      )}

      {(data.rincian || []).length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Baris item</th><th className="py-1 pr-2">Komponen</th>
                <th className="py-1 pr-2 text-right">Qty</th>
                {data.lihat_rupiah && <th className="py-1 pr-2 text-right">Tarif</th>}
                {data.lihat_rupiah && <th className="py-1 pr-2 text-right">Jumlah</th>}
                {bolehKoreksi && <th />}
              </tr>
            </thead>
            <tbody>
              {data.rincian.map((r) => (
                <tr key={`${r.fa_item_id}-${r.work_type_id}`} className="border-t border-border">
                  <td className="py-1 pr-2">{itemNama[r.fa_item_id] || "—"}</td>
                  <td className="py-1 pr-2">{r.label}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{r.qty}</td>
                  {data.lihat_rupiah && <td className="py-1 pr-2 text-right tabular-nums">{rp(r.rate_idr)}</td>}
                  {data.lihat_rupiah && <td className="py-1 pr-2 text-right tabular-nums">{rp(r.amount_idr)}</td>}
                  {bolehKoreksi && (
                    <td className="py-1 text-right">
                      {/* T105 (Owner 1 Nov 2026 "tombol koreksi tidak nampak karena sesama warna biru"): bentuk tombol
                          jelas — garis tepi + teks warna primary di atas latar halaman (kontras WCAG AA terang & gelap). */}
                      <Button size="sm" variant="outline" data-testid={`koreksi-tarif-${r.work_type_id}`}
                              className="border-primary bg-background text-primary hover:bg-primary hover:text-primary-foreground"
                              onClick={() => setKoreksi({ work_type_id: r.work_type_id, label: r.label, rate: r.rate_idr })}>
                        Koreksi
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.lihat_rupiah && (
        <div className="flex flex-wrap gap-6 text-sm">
          <div>Rencana upah (HPP): <b className="tabular-nums">{rp(data.upah_rencana_idr)}</b></div>
          <div className="text-muted-foreground">Sudah tercatat serah terima: <span className="tabular-nums">{rp(data.upah_tercatat_idr)}</span></div>
        </div>
      )}

      {koreksi && (
        <div className="rounded border border-border p-3 space-y-2 text-sm" data-testid="form-koreksi-tarif">
          <div>Koreksi tarif <b>{koreksi.label}</b> (tarif beku {rp(koreksi.rate)}). Upah yang sudah dibayar tidak
            diubah — selisihnya masuk rekap sebagai baris tersendiri, setelah disetujui Owner.</div>
          <Input inputMode="numeric" placeholder="Tarif benar (Rp)" value={benar} onChange={(e) => setBenar(e.target.value)} />
          <Input placeholder="Alasan (wajib, min. 10 karakter)" value={alasan} onChange={(e) => setAlasan(e.target.value)} />
          <div className="flex gap-2">
            <Button size="sm" onClick={ajukanKoreksi}>Ajukan koreksi</Button>
            <Button size="sm" variant="outline" onClick={() => setKoreksi(null)}>Batal</Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="text-sm font-medium">Group jahit yang ditugaskan</div>
        {bolehTugaskan ? (
          <>
            <div className="flex flex-wrap gap-3">
              {regionGroups.map((g) => (
                <label key={g.id} className="flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={(pilihGroup || []).includes(g.id)}
                         onChange={(e) => setPilihGroup((p) => (e.target.checked ? [...(p || []), g.id]
                           : (p || []).filter((x) => x !== g.id)))} />
                  {g.name || g.code}
                </label>
              ))}
              {regionGroups.length === 0 && <span className="text-sm text-muted-foreground">Belum ada group jahit di wilayah ini.</span>}
            </div>
            <Button size="sm" variant="outline" onClick={simpanGroup} data-testid="btn-simpan-group-jahit">Simpan group</Button>
          </>
        ) : (
          <div className="text-sm">
            {(data.group_tertugas || []).map((g) => g.name || g.code).join(", ") || "Belum ditugaskan"}
          </div>
        )}
      </div>
    </Card>
  );
}
